import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  LogOut,
  RefreshCw,
  CheckCircle2,
  Clock as ClockIcon,
  Loader2,
  ArrowLeft,
  Sparkles,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SERVICES } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "asp_admin_token";

// Mirror of backend UPSELL_RECOMMENDATIONS for preview UX
const UPSELL_MAP = {
  doorbell: "smart_lock",
  smart_lock: "security_camera",
  security_camera: "alarm_system",
  alarm_system: "security_camera",
  smart_thermostat: "smart_lock",
  ceiling_fan: "home_media",
  home_media: "ceiling_fan",
};
const SERVICE_LOOKUP = Object.fromEntries(SERVICES.map((s) => [s.id, s]));

const inputClasses =
  "bg-white/5 border-white/10 text-white placeholder:text-white/35 focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] rounded-lg h-11";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null); // booking object or null
  const [completing, setCompleting] = useState(false);

  const loadData = useCallback(
    async (authToken) => {
      const t = authToken ?? token;
      if (!t) return;
      setLoading(true);
      try {
        const headers = { "X-Admin-Password": t };
        const [bk, st] = await Promise.all([
          axios.get(`${API}/admin/bookings`, { headers }),
          axios.get(`${API}/admin/stats`, { headers }),
        ]);
        setBookings(bk.data);
        setStats(st.data);
      } catch (e) {
        if (e?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
          toast.error("Session expired. Please sign in again.");
        } else {
          toast.error("Could not load bookings.");
        }
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) loadData(token);
  }, [token, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const newToken = res.data.token;
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      setPassword("");
      toast.success("Welcome back.");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Invalid password."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings([]);
    setStats(null);
  };

  const confirmComplete = async () => {
    if (!completeTarget) return;
    setCompleting(true);
    try {
      await axios.post(
        `${API}/admin/bookings/${completeTarget.id}/complete`,
        { send_upsell: true, origin_url: window.location.origin },
        { headers: { "X-Admin-Password": token } }
      );
      const recId = UPSELL_MAP[completeTarget.service_id];
      const recTitle = SERVICE_LOOKUP[recId]?.title || "next install";
      toast.success(`Marked complete · 20% off ${recTitle} sent to ${completeTarget.email}`);
      setCompleteTarget(null);
      await loadData();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not mark complete. Try again."
      );
    } finally {
      setCompleting(false);
    }
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0A0A0C] text-white px-6">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-[#D4AF37] mb-8"
            data-testid="admin-back-home"
          >
            <ArrowLeft size={15} /> Back to site
          </Link>

          <div className="flex items-center gap-2 mb-7">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#D4AF37] text-black">
              <ShieldCheck size={18} strokeWidth={2.2} />
            </span>
            <span className="font-semibold tracking-tight text-white text-lg">
              All Service <span className="text-[#D4AF37]">Proz</span>
            </span>
          </div>

          <p className="text-xs uppercase tracking-[0.28em] text-[#D4AF37] font-semibold mb-3">
            Admin Console
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tighter mb-2">
            Sign in to manage bookings.
          </h1>
          <p className="text-white/55 text-sm mb-8">
            Restricted to authorized All Service Proz staff.
          </p>

          <form
            onSubmit={handleLogin}
            data-testid="admin-login-form"
            className="bg-[#121216] border border-white/10 rounded-2xl p-6 md:p-8 space-y-5"
          >
            <div className="space-y-2">
              <Label className="text-white/80 flex items-center gap-2">
                <Lock size={14} className="text-[#D4AF37]" /> Admin password
              </Label>
              <Input
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className={inputClasses}
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#C5A017] font-semibold rounded-full h-11"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white">
      <header className="sticky top-0 z-30 bg-[#0A0A0C]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#D4AF37] text-black">
              <ShieldCheck size={18} strokeWidth={2.2} />
            </span>
            <div>
              <div className="font-semibold tracking-tight text-white">
                All Service <span className="text-[#D4AF37]">Proz</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">
                Admin Console
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-refresh"
              onClick={() => loadData()}
              variant="outline"
              size="sm"
              className="bg-transparent border-white/15 text-white/80 hover:bg-white/5 hover:text-white rounded-full"
            >
              <RefreshCw size={14} className="mr-1.5" /> Refresh
            </Button>
            <Button
              data-testid="admin-logout"
              onClick={logout}
              size="sm"
              className="bg-white/5 hover:bg-white/10 text-white rounded-full"
            >
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          <StatCard label="Total Bookings" value={stats?.total_bookings ?? "—"} />
          <StatCard label="Deposits Paid" value={stats?.deposits_paid ?? "—"} accent />
          <StatCard label="Awaiting Deposit" value={stats?.deposits_unpaid ?? "—"} />
          <StatCard
            label="Deposit Revenue"
            value={
              stats ? `$${Number(stats.deposit_revenue).toFixed(0)}` : "—"
            }
            accent
          />
          <StatCard label="Jobs Completed" value={stats?.jobs_completed ?? "—"} />
          <StatCard label="Upsells Sent" value={stats?.upsells_sent ?? "—"} accent />
        </div>

        {/* Bookings */}
        <div className="rounded-2xl bg-[#121216] border border-white/10 overflow-hidden">
          <div className="px-6 py-5 flex items-center justify-between border-b border-white/10">
            <div>
              <h2 className="text-xl font-semibold">Recent Bookings</h2>
              <p className="text-xs text-white/45 mt-1">
                Sorted by newest first
              </p>
            </div>
            {loading && (
              <Loader2 size={18} className="animate-spin text-[#D4AF37]" />
            )}
          </div>

          <div className="overflow-x-auto">
            <Table data-testid="admin-bookings-table">
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/55">Customer</TableHead>
                  <TableHead className="text-white/55">Service</TableHead>
                  <TableHead className="text-white/55">When</TableHead>
                  <TableHead className="text-white/55">Contact</TableHead>
                  <TableHead className="text-white/55">Deposit</TableHead>
                  <TableHead className="text-white/55">Status</TableHead>
                  <TableHead className="text-white/55 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.length === 0 && !loading && (
                  <TableRow className="border-white/10">
                    <TableCell colSpan={7} className="text-center text-white/45 py-12">
                      No bookings yet.
                    </TableCell>
                  </TableRow>
                )}
                {bookings.map((b) => (
                  <TableRow
                    key={b.id}
                    data-testid={`admin-booking-row-${b.id}`}
                    className="border-white/10 hover:bg-white/[0.03]"
                  >
                    <TableCell className="font-medium">
                      <div className="text-white">{b.customer_name}</div>
                      <div className="text-xs text-white/45 max-w-[200px] truncate">
                        {b.address}
                      </div>
                    </TableCell>
                    <TableCell className="text-white/80">{b.service_title}</TableCell>
                    <TableCell className="text-white/80">
                      <div>{b.booking_date}</div>
                      <div className="text-xs text-white/45">{b.booking_time}</div>
                    </TableCell>
                    <TableCell className="text-white/70 text-sm">
                      <div>{b.email}</div>
                      <div className="text-xs text-white/45">{b.phone}</div>
                    </TableCell>
                    <TableCell>
                      <DepositBadge status={b.deposit_status} amount={b.deposit_amount} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge booking={b} />
                    </TableCell>
                    <TableCell className="text-right">
                      {b.status !== "completed" ? (
                        <Button
                          data-testid={`mark-complete-${b.id}`}
                          onClick={() => setCompleteTarget(b)}
                          size="sm"
                          className="bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37] hover:text-black rounded-full font-semibold"
                        >
                          <CheckCircle2 size={13} className="mr-1.5" />
                          Mark Complete
                        </Button>
                      ) : (
                        <span className="text-xs text-white/40">
                          {b.completed_at
                            ? new Date(b.completed_at).toLocaleDateString()
                            : "Done"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Confirm Mark Complete + upsell preview */}
      <AlertDialog
        open={!!completeTarget}
        onOpenChange={(o) => !o && !completing && setCompleteTarget(null)}
      >
        <AlertDialogContent
          data-testid="complete-dialog"
          className="bg-[#121216] border-white/10 text-white max-w-md rounded-2xl"
        >
          <AlertDialogHeader>
            <div className="grid place-items-center w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 mb-2">
              <Sparkles className="text-[#D4AF37]" size={20} />
            </div>
            <AlertDialogTitle className="text-white text-xl font-semibold tracking-tight">
              Mark this job complete?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/55">
              {completeTarget && (() => {
                const recId = UPSELL_MAP[completeTarget.service_id];
                const rec = SERVICE_LOOKUP[recId];
                if (!rec) return "This will mark the booking as completed.";
                return (
                  <>
                    We'll send <span className="text-white font-medium">{completeTarget.customer_name.split(" ")[0]}</span> a
                    14-day <span className="text-[#D4AF37] font-semibold">20% off</span> code for{" "}
                    <span className="text-white">{rec.title}</span> — your highest-attach add-on after a{" "}
                    {completeTarget.service_title.toLowerCase()}.
                  </>
                );
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {completeTarget && (() => {
            const recId = UPSELL_MAP[completeTarget.service_id];
            const rec = SERVICE_LOOKUP[recId];
            if (!rec) return null;
            const discounted = Math.max(0, Math.floor(rec.starting_price * 0.8));
            return (
              <div
                data-testid="upsell-preview"
                className="mt-2 mb-2 p-4 rounded-xl bg-[#1A1A20] border border-[#D4AF37]/25"
              >
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                  Upsell Preview
                </p>
                <p className="mt-1 text-white font-medium">{rec.title}</p>
                <p className="mt-1 text-sm">
                  <span className="text-white/40 line-through">${rec.starting_price}</span>
                  <span className="ml-2 text-[#D4AF37] font-semibold">from ${discounted}</span>
                  <span className="ml-2 text-white/50">· expires in 14 days</span>
                </p>
                <p className="mt-2 text-xs text-white/45 break-all">→ {completeTarget.email}</p>
              </div>
            );
          })()}

          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              data-testid="complete-dialog-cancel"
              disabled={completing}
              className="bg-transparent border-white/15 text-white/80 hover:bg-white/5 hover:text-white rounded-full"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="complete-dialog-confirm"
              onClick={(e) => {
                e.preventDefault();
                confirmComplete();
              }}
              disabled={completing}
              className="bg-[#D4AF37] text-black hover:bg-[#C5A017] font-semibold rounded-full"
            >
              {completing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send size={14} className="mr-1.5" /> Complete &amp; Send Offer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatusBadge({ booking }) {
  if (booking.status === "completed") {
    return (
      <div className="flex flex-col gap-1">
        <Badge className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/15 rounded-full w-fit">
          <CheckCircle2 size={12} className="mr-1" /> Completed
        </Badge>
        {booking.upsell_sent && (
          <span className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-semibold flex items-center gap-1">
            <Sparkles size={10} /> Upsell sent
          </span>
        )}
      </div>
    );
  }
  return (
    <Badge className="bg-white/5 text-white/65 border border-white/10 hover:bg-white/10 rounded-full">
      <ClockIcon size={12} className="mr-1" /> Pending
    </Badge>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div
      className={cn(
        "p-5 rounded-2xl border bg-[#121216]",
        accent ? "border-[#D4AF37]/30" : "border-white/10"
      )}
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 font-semibold">
        {label}
      </div>
      <div
        className={cn(
          "mt-2 text-3xl font-semibold tracking-tight",
          accent ? "text-[#D4AF37]" : "text-white"
        )}
      >
        {value}
      </div>
    </div>
  );
}

function DepositBadge({ status, amount }) {
  if (status === "paid") {
    return (
      <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/20 rounded-full">
        <CheckCircle2 size={12} className="mr-1" /> Paid ${Number(amount).toFixed(0)}
      </Badge>
    );
  }
  return (
    <Badge className="bg-white/5 text-white/65 border border-white/10 hover:bg-white/10 rounded-full">
      <ClockIcon size={12} className="mr-1" /> Pending
    </Badge>
  );
}
